/**
 * REST client for the widget half of the API (`/api/v1/widget/*`).
 *
 * The widget has no user login. It trades the app's public key for a
 * short-lived visitor token welded to appId + visitorId, then carries that
 * token as a bearer. The token reaches exactly one visitor's own conversation.
 */

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function createApi({ apiBase, publicKey }) {
  const base = `${apiBase}/api/v1`;
  let token = null;

  async function request(path, { method = 'GET', body, auth = true, query } = {}) {
    const url = new URL(`${base}${path}`, window.location.href);
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }

    const headers = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth && token) headers.Authorization = `Bearer ${token}`;

    let res;
    try {
      res = await fetch(url.toString(), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        // No cookies: the visitor token is the only credential, and third-party
        // cookies would not survive the merchant's admin anyway.
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (cause) {
      throw new ApiError(0, 'network_error', 'Could not reach support', { cause: String(cause) });
    }

    if (res.status === 204) return null;

    let payload = null;
    try {
      payload = await res.json();
    } catch {
      /* non-JSON error page */
    }

    if (!res.ok || payload?.ok === false) {
      const error = payload?.error ?? {};
      throw new ApiError(
        res.status,
        error.code ?? 'http_error',
        error.message ?? `Request failed (${res.status})`,
        error.details
      );
    }

    return { data: payload?.data ?? null, meta: payload?.meta ?? null };
  }

  return {
    get token() {
      return token;
    },
    setToken(value) {
      token = value;
    },

    /** publicKey + Origin -> visitor token. Also returns the app's settings. */
    async session({ visitorId, visitor, context } = {}) {
      const { data } = await request('/widget/session', {
        method: 'POST',
        auth: false,
        body: {
          publicKey,
          ...(visitorId ? { visitorId } : {}),
          visitor: visitor ?? {},
          context: context ?? {},
        },
      });
      token = data.token;
      return data;
    },

    /** Appends page views to the session trail, and optionally corrects the
     *  top-level URL the widget could only infer. */
    async activity(pages, topUrl) {
      const { data } = await request('/widget/activity', {
        method: 'POST',
        body: { pages, ...(topUrl ? { topUrl } : {}) },
      });
      return data;
    },

    async me() {
      const { data } = await request('/widget/me');
      return data;
    },

    /** Newest first; the UI reverses for display. */
    async messages({ cursor, limit = 30 } = {}) {
      const { data, meta } = await request('/widget/messages', { query: { cursor, limit } });
      return { messages: data, meta };
    },

    async send({ body, attachments, isEmergency = false, clientId }) {
      const { data } = await request('/widget/messages', {
        method: 'POST',
        body: { body, attachments, isEmergency, clientId },
      });
      return data;
    },

    /**
     * Attachments. The API only ever hands out URLs: `signUpload` returns a
     * presigned PUT the browser uploads to directly, `uploadLink` trades an
     * object key for a short-lived read URL. Neither carries file bytes.
     */
    async signUpload({ fileName, mimeType, size }) {
      const { data } = await request('/widget/uploads', {
        method: 'POST',
        body: { fileName, mimeType, size },
      });
      return data;
    },

    async uploadLink(key, { download = false } = {}) {
      const { data } = await request('/widget/uploads/link', {
        query: { key, download: download ? 'true' : 'false' },
      });
      return data;
    },

    async markRead() {
      const { data } = await request('/widget/read', { method: 'POST' });
      return data;
    },

    async updateProfile(profile) {
      const { data } = await request('/widget/profile', { method: 'PATCH', body: profile });
      return data;
    },

    async subscribePush(subscription) {
      const { data } = await request('/widget/push/subscribe', {
        method: 'POST',
        body: subscription,
      });
      return data;
    },

    async unsubscribePush(endpoint) {
      await request('/widget/push/unsubscribe', { method: 'POST', body: { endpoint } });
    },

    /**
     * Notifies every agent on this app — socket for whoever has the dashboard
     * open, Web Push for every registered agent device. Persists nothing.
     */
    async notifyAgents(isEmergency = false) {
      const { data } = await request('/widget/notify/agents', {
        method: 'POST',
        body: { isEmergency },
      });
      return data;
    },

    /** Self-serve help centre. Readable before a session exists. */
    async docs({ q, category, limit = 6 } = {}) {
      const { data } = await request('/widget/docs', {
        auth: false,
        query: { publicKey, q, category, limit },
      });
      return data;
    },

    async doc(slug) {
      const { data } = await request(`/widget/docs/${encodeURIComponent(slug)}`, {
        auth: false,
        query: { publicKey },
      });
      return data;
    },
  };
}

export default createApi;
